import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { useAuth } from '@/context/AuthContext';
import { paths } from '@/routes/paths';
import styles from './Sso.module.css';

/**
 * Where playTUB drops a learner who chose "TUB Marketplace" from its profile menu.
 *
 * They are already signed in over there, so they should not be asked again here.
 * playTUB mints a one-time ticket, sends them to this route with it, and this page
 * spends it — the backend redeems it against the auth service and answers with the
 * same session a password login would have produced.
 *
 * The ticket arrives in the URL **fragment**: fragments are never sent to a server,
 * so it stays out of access logs and Referer headers on the way in. It is stripped
 * from the address bar before the exchange, so it does not sit in history or get
 * re-sent by a refresh.
 *
 * A failure here is not a dead end. The ticket is short-lived and single-use, so a
 * refreshed or forwarded link legitimately fails — the page says so and offers the
 * shop, where signing in works as it always has.
 */

/** Reads and removes the ticket, so the URL is clean before anything is redeemed. */
function takeTicket() {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const ticket = new URLSearchParams(hash).get('ticket');
  if (ticket) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  return ticket;
}

export function Sso() {
  const { signInWithTicket } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  // A ticket can only be spent once, and StrictMode runs effects twice in dev —
  // without this guard the second run redeems an already-spent ticket and reports
  // a failed sign-in that actually succeeded.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const ticket = takeTicket();
    if (!ticket) {
      setError('This sign-in link is incomplete. Please open the marketplace from playTUB again.');
      return;
    }

    signInWithTicket(ticket)
      .then(() => navigate(paths.home, { replace: true }))
      .catch((err) => setError(err.message ?? 'That sign-in link has expired. Please sign in.'));
  }, [navigate, signInWithTicket]);

  if (error) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>We couldn&apos;t sign you in</h1>
        <p className={styles.copy}>{error}</p>
        <Button to={paths.home}>Go to the shop →</Button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.spinner} aria-hidden="true" />
      <p className={styles.copy} role="status">
        Signing you in…
      </p>
    </div>
  );
}
